import React, { useMemo, useState } from 'react';
import {
  Table, TableColumn, TableSort, CorrectionBar, StagePill, Pill,
} from '../../components/ui';
import Section from './Section';

/**
 * /design — Table system (TASK-104): v2 table language next to the Dashboard
 * v2 active-RFQ table and the Step 2 BOM modal mocks. Sortable headers,
 * selection, density, sticky header, empty/loading, correction-bar footer.
 */
interface Program {
  id: string;
  name: string;
  owner: string;
  stage: 1 | 2 | 3 | 4 | 5;
  stageLabel: string;
  parts: number;
  updated: string;
}

const PROGRAMS: Program[] = [
  { id: 'RFQ-2026-0141', name: 'AMR Drive Train Sourcing', owner: 'Keerthi V.', stage: 4, stageLabel: 'Outreach', parts: 34, updated: '2h ago' },
  { id: 'RFQ-2026-0139', name: 'Vision System Refresh', owner: 'Priya N.', stage: 5, stageLabel: 'Quotes', parts: 12, updated: '5h ago' },
  { id: 'RFQ-2026-0137', name: 'Gripper Assembly v3', owner: 'Marcus T.', stage: 3, stageLabel: 'Discovery', parts: 21, updated: '1d ago' },
  { id: 'RFQ-2026-0134', name: 'AGV Battery & BMS Package', owner: 'Marcus T.', stage: 1, stageLabel: 'Upload BOM', parts: 9, updated: '5d ago' },
];

interface BomRow {
  no: string;
  component: string;
  part: string;
  description: string;
  qty: number;
  unit: string;
}

const BOM: BomRow[] = [
  { no: '01', component: 'Hub motor, brushless DC, 48V 200W', part: 'HM-4820-BLDC', description: 'Direct-drive wheel motor for AMR drive train', qty: 4, unit: 'pcs' },
  { no: '02', component: 'Optical encoder, 2048 PPR, ABZ', part: 'ENC-2048-OPT', description: 'Shaft position feedback for closed-loop control', qty: 2, unit: 'pcs' },
  { no: '03', component: 'Motor driver, 3-phase gate, 100V', part: 'DRV-8353-EVM', description: 'Gate driver stage for the BLDC hub motors', qty: 4, unit: 'pcs' },
  { no: '04', component: 'Deep-groove ball bearing, sealed', part: 'BRG-6204-2RS', description: 'Sealed bearing for drive shaft, 20mm bore', qty: 8, unit: 'pcs' },
  { no: '05', component: 'Power cable, AWG12, silicone', part: 'CBL-PWR-AWG12', description: 'High-flex battery-to-driver power run', qty: 6, unit: 'm' },
  { no: '06', component: '9-DOF IMU module, I2C/SPI', part: 'IMU-BNO085', description: 'Odometry correction and tilt sensing', qty: 1, unit: 'pcs' },
];

const programColumns = (compact?: boolean): TableColumn<Program>[] => [
  {
    key: 'name',
    header: 'Program',
    sortable: true,
    render: (row) => (
      <span className="min-w-0">
        <span className="block truncate text-row-title text-ink">{row.name}</span>
        {!compact && (
          <span className="block truncate font-mono text-[11px] text-ink-muted">{row.id} · {row.owner}</span>
        )}
      </span>
    ),
  },
  {
    key: 'stage',
    header: 'Stage',
    width: '120px',
    sortable: true,
    render: (row) => <StagePill stage={row.stage}>{row.stage} · {row.stageLabel}</StagePill>,
  },
  { key: 'parts', header: 'Parts', width: '56px', align: 'right', mono: true, sortable: true },
  { key: 'updated', header: 'Updated', width: '80px', render: (row) => <span className="text-body-sm text-ink-muted">{row.updated}</span> },
];

const bomColumns: TableColumn<BomRow>[] = [
  { key: 'no', header: '#', width: '38px', mono: true, render: (r) => <span className="text-ink-muted">{r.no}</span> },
  {
    key: 'component',
    header: 'Component',
    render: (r) => (
      <span className="truncate font-semibold text-ink">
        {r.component} <span className="font-mono text-meta font-normal text-ink-muted">· {r.part}</span>
      </span>
    ),
  },
  { key: 'description', header: 'Description', width: 'minmax(0,1.3fr)', render: (r) => <span className="truncate text-ink-secondary">{r.description}</span> },
  { key: 'qty', header: 'Qty', width: '48px', align: 'right', mono: true },
  { key: 'unit', header: 'Unit', width: '48px', render: (r) => <span className="text-ink-secondary">{r.unit}</span> },
];

const TableGallery: React.FC = () => {
  const [sort, setSort] = useState<TableSort>({ key: 'name', direction: 'asc' });
  const [selectedKey, setSelectedKey] = useState<string>('RFQ-2026-0139');

  const sorted = useMemo(() => {
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...PROGRAMS].sort((a, b) => {
      const va = a[sort.key as keyof Program];
      const vb = b[sort.key as keyof Program];
      return (typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb))) * dir;
    });
  }, [sort]);

  return (
    <>
      <Section title="Table — sortable headers, row click + selection">
        <Table
          aria-label="Active programs"
          columns={programColumns()}
          rows={sorted}
          rowKey={(row) => row.id}
          sort={sort}
          onSortChange={setSort}
          selectedKeys={[selectedKey]}
          onRowClick={(row) => setSelectedKey(row.id)}
        />
        <p className="mt-s4 text-meta text-ink-muted">
          10.5/600 uppercase headers · 13px rows · mono data columns · hover tint · click to select.
        </p>
      </Section>

      <Section title="Table — compact density">
        <Table
          aria-label="Active programs, compact"
          columns={programColumns(true)}
          rows={PROGRAMS}
          rowKey={(row) => row.id}
          density="compact"
        />
      </Section>

      <Section title="Table — BOM modal: sticky header, scroll, correction-bar slot">
        <Table
          aria-label="Extracted BOM"
          columns={bomColumns}
          rows={BOM}
          rowKey={(row) => row.no}
          density="compact"
          stickyHeader
          maxHeight={180}
          footer={
            <CorrectionBar
              secondaryLabel="Done"
              onAction={() => undefined /* wiring to Robbie chat lands in TASK-304 */}
            />
          }
        />
        <p className="mt-s4 text-meta text-ink-muted">
          The footer slot takes any node; CorrectionBar is the shipped pattern — violet only on the Robbie action.
        </p>
      </Section>

      <Section title="Table — loading + empty">
        <div className="grid grid-cols-1 gap-s6 lg:grid-cols-2">
          <Table aria-label="Loading table" columns={programColumns(true)} rows={[]} rowKey={() => ''} loading skeletonRows={4} />
          <Table
            aria-label="Empty table"
            columns={programColumns(true)}
            rows={[]}
            rowKey={() => ''}
            empty={
              <span className="inline-flex flex-col items-center gap-s2">
                <Pill status="neutral">No RFQs yet</Pill>
                <span>Upload a BOM to start your first sourcing run.</span>
              </span>
            }
          />
        </div>
      </Section>
    </>
  );
};

export default TableGallery;
